/**
 * Custom hook for session connection actions
 *
 * Bridges the session-first UX with the existing connection system by
 * converting sessions to endpoints and delegating to connectionManager.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ERROR_MESSAGES } from '@/constants/terminology';
import { connectionManager } from '@/services/connectionManager';
import { createScopedReporter } from '@/services/errorReporter';
import { sessionToEndpoints, getEndpointIdsForSession } from '@/services/sessionAdapter';
import { useActiveConnectionsStore } from '@/stores/activeConnectionsStore';
import { useSessionsStore } from '@/stores/sessionsStore';

const reporter = createScopedReporter('SESSION');

/**
 * Provides handlers for session connect, disconnect, delete, and roll actions
 *
 * Converts sessions to endpoints internally and uses the existing
 * connectionManager infrastructure for WebSocket management.
 *
 * @returns Object with session action handlers
 */
export function useSessionActions() {
  const navigate = useNavigate();
  const { deleteSession, markAsUsed, getSession, setLastConnectedSession, enableAutoConnect, disableAutoConnect } = useSessionsStore();
  const { canConnect } = useActiveConnectionsStore();

  /**
   * Connect all endpoints derived from a session
   */
  const handleConnect = useCallback(
    async (sessionId: string) => {
      const session = getSession(sessionId);
      if (!session) {
        reporter.error('Session not found', { context: { sessionId } });
        return;
      }

      if (!canConnect()) {
        reporter.warning(ERROR_MESSAGES.MAX_CONNECTIONS);
        return;
      }

      const endpoints = sessionToEndpoints(session);
      if (endpoints.length === 0) {
        reporter.error('No endpoints for session', { context: { sessionId } });
        return;
      }

      try {
        for (const endpoint of endpoints) {
          await connectionManager.connect(endpoint);
        }
        markAsUsed(session.id);
        setLastConnectedSession(session.id);
        enableAutoConnect(session.id);
      } catch (error) {
        reporter.error('Failed to connect session', {
          originalError: error instanceof Error ? error : undefined,
          context: { sessionId: session.id }
        });
      }
    },
    [canConnect, markAsUsed, getSession, setLastConnectedSession, enableAutoConnect]
  );

  /**
   * Disconnect all endpoints derived from a session
   */
  const handleDisconnect = useCallback(
    (sessionId: string) => {
      const session = getSession(sessionId);
      if (!session) {
        reporter.error('Session not found for disconnect', { context: { sessionId } });
        return;
      }

      try {
        const endpointIds = getEndpointIdsForSession(sessionId);
        for (const endpointId of endpointIds) {
          connectionManager.disconnect(endpointId);
        }
        disableAutoConnect(sessionId);
      } catch (error) {
        reporter.error('Failed to disconnect session', {
          originalError: error instanceof Error ? error : undefined,
          context: { sessionId }
        });
      }
    },
    [getSession, disableAutoConnect]
  );

  /**
   * Disconnect and remove a session
   */
  const handleDelete = useCallback(
    (sessionId: string) => {
      const session = getSession(sessionId);
      if (session) {
        const endpointIds = getEndpointIdsForSession(sessionId);
        for (const endpointId of endpointIds) {
          try {
            connectionManager.disconnect(endpointId);
          } catch {
            // Ignore disconnect errors during delete
          }
        }
      }
      disableAutoConnect(sessionId);
      deleteSession(sessionId);
    },
    [deleteSession, getSession, disableAutoConnect]
  );

  /**
   * Navigate to the roll page
   */
  const handleRoll = useCallback(() => {
    navigate('/roll');
  }, [navigate]);

  return {
    handleConnect,
    handleDisconnect,
    handleDelete,
    handleRoll
  };
}
